import React, { Component } from 'react';
import { Tabs, Tab, Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'components/controls/select';
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import 'ace-builds/webpack-resolver';
import Preferences from "context/preferences";

function getTimezoneOptions() {
  const options = [{ value: "UTC", label: "UTC" }];
  try {
    const timezones = Intl.supportedValuesOf('timeZone');
    for (const tz of timezones) {
      options.push({ value: tz, label: tz });
    }
  } catch (err) {
    // Fallback: UTC-only list
  }
  return options;
}

const TIMEZONE_OPTIONS = getTimezoneOptions();

const TAB_OPTIONS = [
  { value: "workflow", label: "Workflow" },
  { value: "debugger", label: "Debugger" },
  { value: "playground", label: "Playground" },
  { value: "revisions", label: "Revisions" },
  { value: "settings", label: "Settings" }
];

function scopeLabel(scope) {
  if (scope && scope.blobId) return "blob";
  if (scope && scope.orgId) return "organization";
  return "user";
}

export default class PreferencesEditor extends Component {
  constructor(props) {
    super(props);

    const raw = Preferences.getRaw(this.props.scope);
    this.state = {
      activeTab: "general",
      timezone: Preferences.get("app.timezone", this.props.scope),
      blobHeaderCollapsed: Preferences.get("app.blobHeaderCollapsed", this.props.scope),
      editorDetailsPane: Preferences.get("blob:workflow.editorDetailsPane", this.props.scope),
      debuggerDetailsPane: Preferences.get("blob:workflow.debuggerDetailsPane", this.props.scope),
      playgroundDetailsPane: Preferences.get("blob:workflow.playgroundDetailsPane", this.props.scope),
      defaultTab: Preferences.get("blob:workflow.defaultTab", this.props.scope),
      autoSelectRecentSession: Preferences.get("blob:workflow.autoSelectRecentSession", this.props.scope),
      jsonText: JSON.stringify(raw, null, 2),
      jsonValid: true,
      jsonDirty: false,
      applied: false
    };
  }

  componentDidMount() {
    this.unsubscribe = Preferences.subscribe(this.refreshFromStore);
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe();
  }

  refreshFromStore = () => {
    this.setState({
      timezone: Preferences.get("app.timezone", this.props.scope),
      blobHeaderCollapsed: Preferences.get("app.blobHeaderCollapsed", this.props.scope),
      editorDetailsPane: Preferences.get("blob:workflow.editorDetailsPane", this.props.scope),
      debuggerDetailsPane: Preferences.get("blob:workflow.debuggerDetailsPane", this.props.scope),
      playgroundDetailsPane: Preferences.get("blob:workflow.playgroundDetailsPane", this.props.scope),
      defaultTab: Preferences.get("blob:workflow.defaultTab", this.props.scope),
      autoSelectRecentSession: Preferences.get(
        "blob:workflow.autoSelectRecentSession", this.props.scope),
      jsonText: JSON.stringify(Preferences.getRaw(this.props.scope), null, 2),
      jsonDirty: false
    });
  }

  handleTabSelect = (key) => {
    const updates = { activeTab: key, applied: false };
    if (key === "general") {
      updates.timezone = Preferences.get("app.timezone", this.props.scope);
      updates.blobHeaderCollapsed = Preferences.get("app.blobHeaderCollapsed", this.props.scope);
      updates.editorDetailsPane = Preferences.get("blob:workflow.editorDetailsPane", this.props.scope);
      updates.debuggerDetailsPane = Preferences.get("blob:workflow.debuggerDetailsPane", this.props.scope);
      updates.playgroundDetailsPane = Preferences.get("blob:workflow.playgroundDetailsPane", this.props.scope);
      updates.defaultTab = Preferences.get("blob:workflow.defaultTab", this.props.scope);
      updates.autoSelectRecentSession = Preferences.get(
        "blob:workflow.autoSelectRecentSession", this.props.scope);
    } else if (key === "advanced") {
      updates.jsonText = JSON.stringify(Preferences.getRaw(this.props.scope), null, 2);
      updates.jsonDirty = false;
    }
    this.setState(updates);
  }

  handleTimezoneChange = (option) => {
    Preferences.set("app.timezone", option.value, this.props.scope);
    this.setState({
      timezone: option.value,
      jsonText: JSON.stringify(Preferences.getRaw(this.props.scope), null, 2),
      jsonDirty: false
    });
  }

  handleDefaultTabChange = (option) => {
    Preferences.set("blob:workflow.defaultTab", option.value, this.props.scope);
    this.setState({
      defaultTab: option.value,
      jsonText: JSON.stringify(Preferences.getRaw(this.props.scope), null, 2),
      jsonDirty: false
    });
  }

  handleToggle = (prefKey, stateKey) => {
    const newValue = !this.state[stateKey];
    Preferences.set(prefKey, newValue, this.props.scope);
    this.setState({
      [stateKey]: newValue,
      jsonText: JSON.stringify(Preferences.getRaw(this.props.scope), null, 2),
      jsonDirty: false
    });
  }

  handleJsonChange = (newValue) => {
    let valid = true;
    try {
      JSON.parse(newValue);
    } catch (err) {
      valid = false;
    }
    this.setState({
      jsonText: newValue,
      jsonValid: valid,
      jsonDirty: true,
      applied: false
    });
  }

  handleApply = () => {
    try {
      const parsed = JSON.parse(this.state.jsonText);
      Preferences.setRaw(parsed, this.props.scope);
      this.setState({
        timezone: Preferences.get("app.timezone", this.props.scope),
        blobHeaderCollapsed: Preferences.get("app.blobHeaderCollapsed", this.props.scope),
        editorDetailsPane: Preferences.get("blob:workflow.editorDetailsPane", this.props.scope),
        debuggerDetailsPane: Preferences.get("blob:workflow.debuggerDetailsPane", this.props.scope),
        playgroundDetailsPane: Preferences.get("blob:workflow.playgroundDetailsPane", this.props.scope),
        defaultTab: Preferences.get("blob:workflow.defaultTab", this.props.scope),
        autoSelectRecentSession: Preferences.get("blob:workflow.autoSelectRecentSession", this.props.scope),
        jsonDirty: false,
        applied: true
      });
    } catch (err) {
      // Should not happen since Apply is disabled when invalid
    }
  }

  render() {
    const selectedTimezone = TIMEZONE_OPTIONS.find(
      opt => opt.value === this.state.timezone
    ) || { value: "UTC", label: "UTC" };

    const level = scopeLabel(this.props.scope);

    return (
      <>
        <h5>Preferences</h5>
        <hr/>
        <div>
          Preferences defined here apply at the <strong>{level}</strong> level.
          Values set at a more specific level (user &rarr; organization &rarr; blob) take
          precedence over broader ones.
        </div>
        <div className="main-tabs">
          <Tabs activeKey={this.state.activeTab} onSelect={this.handleTabSelect}
            id="preferences-tabs" className="content-row">
            <Tab eventKey="general" title="General">
              <div className="content-row" style={{ paddingTop: "16px" }}>
                <div style={{ marginBottom: "16px" }}>
                  Common preferences with native controls. Changes are saved immediately.
                </div>
                <strong>Application</strong>
                <Form.Group style={{ marginTop: "12px" }}>
                  <Row>
                    <Col sm={4}>
                      <Select
                        options={TIMEZONE_OPTIONS}
                        value={selectedTimezone}
                        onChange={this.handleTimezoneChange}
                        placeholder="Select timezone..."
                        menuPortalTarget={document.body}                      />
                    </Col>
                  </Row>
                  <Form.Text muted>
                    Timezone used for displaying dates and times.
                  </Form.Text>
                </Form.Group>
                <Form.Group style={{ marginTop: "12px" }}>
                  <Form.Check
                    type="switch"
                    id="pref-blob-header-collapsed"
                    label="Collapse blob header"
                    checked={this.state.blobHeaderCollapsed}
                    onChange={() => this.handleToggle(
                      "app.blobHeaderCollapsed", "blobHeaderCollapsed")}
                  />
                  <Form.Text muted>
                    Moves the org/blob title and tab strip into the top navigation bar to save
                    vertical space on blob pages.
                  </Form.Text>
                </Form.Group>
                <hr/>
                {this.props.scope && this.props.scope.blobId &&
                 this.props.blob && "workflow" === this.props.blob.domain &&
                  <>
                  <strong>Workflow Blob</strong>
                  <Form.Group style={{ marginTop: "12px" }}>
                    <Form.Check
                      type="switch"
                      id="pref-editor-details"
                      label="Editor details pane open by default"
                      checked={this.state.editorDetailsPane}
                      onChange={() => this.handleToggle(
                        "blob:workflow.editorDetailsPane", "editorDetailsPane")}
                    />
                    <Form.Check
                      type="switch"
                      id="pref-debugger-details"
                      label="Debugger details pane open by default"
                      checked={this.state.debuggerDetailsPane}
                      onChange={() => this.handleToggle(
                        "blob:workflow.debuggerDetailsPane", "debuggerDetailsPane")}
                    />
                    <Form.Check
                      type="switch"
                      id="pref-playground-details"
                      label="Playground details pane open by default"
                      checked={this.state.playgroundDetailsPane}
                      onChange={() => this.handleToggle(
                        "blob:workflow.playgroundDetailsPane", "playgroundDetailsPane")}
                    />
                    <Form.Text muted>
                      Controls the initial visibility of details panes in workflow views.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group style={{ marginTop: "12px" }}>
                    <Row>
                      <Col sm={4}>
                        <Select
                          options={TAB_OPTIONS}
                          value={TAB_OPTIONS.find(opt => opt.value === this.state.defaultTab)
                            || TAB_OPTIONS[0]}
                          onChange={this.handleDefaultTabChange}
                          menuPortalTarget={document.body}                        />
                      </Col>
                    </Row>
                    <Form.Text muted>
                      Tab shown when opening this blob.
                    </Form.Text>
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    id="pref-auto-select-session"
                    label="Auto-select most recent playground session"
                    checked={this.state.autoSelectRecentSession}
                    onChange={() => this.handleToggle(
                      "blob:workflow.autoSelectRecentSession", "autoSelectRecentSession")}
                  />
                  <Form.Text muted>
                    Automatically loads the most recent session when opening the playground.
                  </Form.Text>
                  <hr/>
                  </>
                }
              </div>
            </Tab>
            <Tab eventKey="advanced" title="Advanced">
              <div className="content-row" style={{ paddingTop: "16px" }}>
                <div style={{ marginBottom: "16px" }}>
                  Raw JSON view of preferences at this level. Edit directly and click Apply to save.
                </div>
                <Row>
                  <Col sm={11}>
                    <AceEditor
                      name="preferences-json-editor"
                      width="100%"
                      height="200px"
                      mode="json"
                      theme="monokai"
                      wrapEnabled={true}
                      value={this.state.jsonText}
                      onChange={this.handleJsonChange}
                    />
                  </Col>
                </Row>
                <div className="content-row">
                  <Button
                    variant="success"
                    size="sm"
                    disabled={!this.state.jsonValid || !this.state.jsonDirty}
                    onClick={this.handleApply}
                  >
                    Apply
                  </Button>
                  {!this.state.jsonValid &&
                    <span className="text-danger" style={{ marginLeft: "12px" }}>
                      Invalid JSON
                    </span>
                  }
                  {this.state.applied &&
                    <span className="text-success" style={{ marginLeft: "12px" }}>
                      Applied
                    </span>
                  }
                </div>
                <div className="content-row">
                  <Form.Text muted>
                    Preferences are stored as flat key-value pairs with dot-notation keys.<br/>
                    Categories: <code>app.*</code> (application),
                    <code> blob:&lt;type&gt;.*</code> (blob-specific),
                    <code> custom.*</code> (user-defined).
                  </Form.Text>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </>
    );
  }
}
